import { emptyIfInvalid } from '../../../utils/value.js'
import type * as Style from '../../index.js'
import { ColorReference, Keyword, Length } from '../../values/index.js'
import { fixReference as fixColorReference } from '../basic/color.js'
import { formatSideDimensions } from '../../../utils/summary.js'

// Border
export enum BorderStyle {
  'solid' = 'solid',
  'dashed' = 'dashed',
  'dotted' = 'dotted',
  'double' = 'double'
}

export const Border = (props: any = {}) => ({
  borderStyle: Keyword<BorderStyle>(props.borderStyle, BorderStyle),
  borderColor: ColorReference(props.borderColor),
  borderLeftWidth: Length(props.borderLeftWidth),
  borderRightWidth: Length(props.borderRightWidth),
  borderTopWidth: Length(props.borderTopWidth),
  borderBottomWidth: Length(props.borderBottomWidth)
})

Border.isValid = (props: Border) =>
  ColorReference.isValid(props.borderColor) &&
  (props.borderLeftWidth.value != 0 ||
    props.borderRightWidth.value != 0 ||
    props.borderTopWidth.value != 0 ||
    props.borderBottomWidth.value != 0)

export type Border = ReturnType<typeof Border>

Border.Fix = (props: Border, styles: Style.Rule[]): Border => ({
  ...Border(props),
  borderColor: fixColorReference(props.borderColor, styles)
})

// Border radius
export const BorderRadius = (props: any = {}) => ({
  borderTopLeftRadius: Length(props.borderTopLeftRadius),
  borderTopRightRadius: Length(props.borderTopRightRadius),
  borderBottomLeftRadius: Length(props.borderBottomLeftRadius),
  borderBottomRightRadius: Length(props.borderBottomRightRadius)
})

BorderRadius.isValid = (props: BorderRadius) =>
  props.borderTopLeftRadius.value != 0 ||
  props.borderTopRightRadius.value != 0 ||
  props.borderBottomLeftRadius.value != 0 ||
  props.borderBottomRightRadius.value != 0

export type BorderRadius = ReturnType<typeof BorderRadius>

BorderRadius.Fix = (props: BorderRadius, styles: Style.Rule[]): BorderRadius => ({
  ...BorderRadius(props)
})

// Box shadow
export const BoxShadow = (props: any = {}) => ({
  boxShadowInset: !!props.boxShadowInset,
  boxShadowOffsetX: Length(props.boxShadowOffsetX),
  boxShadowOffsetY: Length(props.boxShadowOffsetY),
  boxShadowBlurRadius: Length(props.boxShadowBlurRadius),
  boxShadowSpreadRadius: Length(props.boxShadowSpreadRadius),
  boxShadowColor: ColorReference(props.boxShadowColor)
})

BoxShadow.isValid = (props: BoxShadow) =>
  ColorReference.isValid(props.boxShadowColor) &&
  (props.boxShadowOffsetX.value != 0 ||
    props.boxShadowOffsetY.value != 0 ||
    props.boxShadowBlurRadius.value != 0 ||
    props.boxShadowSpreadRadius.value != 0)

export type BoxShadow = ReturnType<typeof BoxShadow>

BoxShadow.Fix = (props: BoxShadow, styles: Style.Rule[]): BoxShadow => ({
  ...BoxShadow(props),
  boxShadowColor: fixColorReference(props.boxShadowColor, styles)
})

export const Props = (props: any = {}) => ({
  ...emptyIfInvalid(props, Border),
  ...emptyIfInvalid(props, BorderRadius),
  ...emptyIfInvalid(props, BoxShadow)
})

export type Props = ReturnType<typeof Props>
export const fix = (props: Props, styles: Style.Rule[]): Props => ({
  ...Border.Fix(props, styles),
  ...BorderRadius.Fix(props, styles),
  ...BoxShadow.Fix(props, styles)
})
export const validate = () => true
export const label = 'Decoration'
export const type = 'decoration'
export const property = 'decorationIds'
export const forms = [
  {
    id: 'decorationRadius',
    label: 'Corner radius',
    extractProperties: (props: Props) => BorderRadius(props),
    validate: BorderRadius.isValid
  },
  {
    id: 'decorationShadow',
    label: 'Drop shadow',
    extractProperties: (props: Props) => BoxShadow(props),
    validate: BoxShadow.isValid
  },
  {
    id: 'decorationBorder',
    label: 'Border',
    extractProperties: (props: Props) => Border(props),
    validate: Border.isValid
  }
]
export const category = 'aspect'
export const summarize = (props: Props, styles?: Style.Rule[], formId?: string) => {
  switch (formId) {
    case 'decorationRadius':
      const radius = formatSideDimensions(
        props.borderTopLeftRadius.value + props.borderTopLeftRadius.unit,
        props.borderTopRightRadius.value + props.borderTopRightRadius.unit,
        props.borderBottomLeftRadius.value + props.borderBottomLeftRadius.unit,
        props.borderBottomRightRadius.value + props.borderBottomRightRadius.unit
      )
      return radius == '9999px' ? 'Rounded' : radius
    case 'decorationShadow':
      return props.boxShadowOffsetX.value || props.boxShadowOffsetY.value
        ? props.boxShadowInset
          ? 'Inner'
          : 'Outer'
        : 'None'
    case 'decorationBorder':
      return `${formatSideDimensions(
        props.borderTopWidth.value + props.borderTopWidth.unit,
        props.borderBottomWidth.value + props.borderBottomWidth.unit,
        props.borderLeftWidth.value + props.borderLeftWidth.unit,
        props.borderRightWidth.value + props.borderRightWidth.unit
      )}`
    default:
      return `${formatSideDimensions(
        props.borderTopWidth.value + props.borderTopWidth.unit,
        props.borderBottomWidth.value + props.borderBottomWidth.unit,
        props.borderLeftWidth.value + props.borderLeftWidth.unit,
        props.borderRightWidth.value + props.borderRightWidth.unit
      )}`
  }
}
